import React, { useMemo, useState, useRef, useEffect } from "react";
import { Row, Col, Label, Input, Badge, Button, Spinner, Alert } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import CommonForm from "Components/Common/CommonForm";
import SectionCard from "Components/Common/SectionCard";
import CommonTableIndexWise, { Column } from "Components/Common/CommonTableIndexWise";
import Reason, { ReasonSubmitPayload } from "common/utilities/Reason/Reason";
import { GetUserData, showToastMessage, formatDate } from "common/service/CommonService";
import { queryClient } from "api/queryClient";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import styles from "../css/dispensing.module.scss";
import {
  AddContainerSelectionProps,
  ContainerDeductionState,
  ContainerItem,
  Step1FormValues,
} from "../helpers/dispensing.types";
import {
  calculateNetUsableContainerQuantity,
  calculateTotalDeductedQuantity,
  calculateTotalInitialQuantity,
  buildSaveDispensingPayload,
  buildUpdateDispensingPayload,
} from "../helpers/dispensing.helper";
import {
  getStudyInformationFields,
  getIpDetailFields,
  getPerSubjectQtyFields,
  getDeductionTableColumns,
} from "../helpers/dispensing.constant";
import { useIpInventoryService } from "../../IPinventory/api/useIpInventoryService";
import { useDispensingLog } from "../api/useDispensingLog";
import { useGlobalStudy } from "context/StudyContext";
import FullPageLoader from "Components/Common/FullPageLoader";

const AddContainerSelection: React.FC<AddContainerSelectionProps> = ({
  editId = null,
  selectedRecord = null,
  isStep1Saved = false,
  setIsStep1Saved,
  onStepChange,
  onSuccessSubmit,
  onCancel,
}) => {
  const { selectedStudy: globalStudy } = useGlobalStudy();

  /* ------------------------------------------------------------------------ */
  /* 1. LOCAL STATE & REFS                                                    */
  /* ------------------------------------------------------------------------ */
  const [deductions, setDeductions] = useState<ContainerDeductionState>({});
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const isPopulatedRef = useRef<boolean>(false);

  /* ------------------------------------------------------------------------ */
  /* 2. REACT HOOK FORM SETUP                                                 */
  /* ------------------------------------------------------------------------ */
  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<Step1FormValues>({
    defaultValues: {
      study: "",
      periodno: null,
      ipcategory: "",
      productname: "",
      batchlotno: "",
      manufacturingdate: "",
      expirydate: "",
      manufacturerlabel: "",
      manufacturername: "",
      unsealcontainers: [],
      persubjectqty: null,
    },
    mode: "onChange",
  });

  /* ------------------------------------------------------------------------ */
  /* 3. FORM WATCHERS                                                         */
  /* ------------------------------------------------------------------------ */
  const selectedStudyId = watch("study");
  const selectedPeriodNo = watch("periodno");
  const selectedIPCategoryVal = watch("ipcategory");
  const selectedContainers: string[] = watch("unsealcontainers") || [];
  const perSubjectQtyVal = watch("persubjectqty");

  /* ------------------------------------------------------------------------ */
  /* 4. MEMOIZED COMPUTATIONS & API QUERIES                                   */
  /* ------------------------------------------------------------------------ */
  const { DropdownQuery, InsertMutation, UpdateMutation } = useDispensingLog({
    getStudyDropDown: true,
  });

  const studyOptions = useMemo(
    () =>
      (DropdownQuery?.data as any)?.data?.map((study: any) => ({
        label: study.studycode,
        value: String(study.studyid),
        subjectno: study.subjectno || [],
      })) || [],
    [DropdownQuery?.data]
  );

  const selectedStudy = useMemo(() => {
    if (!selectedStudyId || !DropdownQuery?.data?.data) return null;
    return (DropdownQuery.data.data as any[]).find(
      (s: any) => String(s.studyid) === String(selectedStudyId)
    );
  }, [selectedStudyId, DropdownQuery?.data]);

  const periodOptions = useMemo(() => {
    const periods = selectedStudy?.periods || [];
    return periods.map((period: { periodid: number; periodnumber: string }) => ({
      label: period.periodnumber,
      value: period.periodid,
    }));
  }, [selectedStudy]);

  const { IpDropdownQuery } = useDispensingLog({
    getIpDropdown: true,
    studyid: Number(selectedStudyId),
    periodid: selectedPeriodNo,
  });

  const ipCategoryOptions = useMemo(() => {
    const inventory = IpDropdownQuery.data?.data?.[0]?.inventory || [];
    return inventory.map((code: any) => ({
      label: code.ipcategory,
      value: code.ipcategory,
    }));
  }, [IpDropdownQuery.data]);

  const { SelectIdQuery } = useIpInventoryService({
    studyid: selectedStudyId || globalStudy?.studyid,
    ipcategory: selectedIPCategoryVal,
  });

  const rawIpData = SelectIdQuery.data?.data;
  const ipData = Array.isArray(rawIpData) ? rawIpData[0] : rawIpData;

  const availableContainers = useMemo(() => {
    const containers = ipData?.containers || [];
    return containers.map((c: any, idx: number) => ({
      ipdetailid: c.ipdetailid || c.containerid || idx + 1,
      containernumber: String(c.containernumber || idx + 1).padStart(2, "0"),
      initialquantity: Number(c.initialquantity),
    }));
  }, [ipData]);

  /* ------------------------------------------------------------------------ */
  /* 5. EFFECTS (useEffect) & GLOBAL STUDY SYNC                               */
  /* ------------------------------------------------------------------------ */

  // Reset Population Flag on Edit ID Change
  useEffect(() => {
    isPopulatedRef.current = false;
  }, [editId]);

  // Sync Global Context Study with Form (Add Mode)
  useEffect(() => {
    if (!editId) {
      if (globalStudy?.studyid && studyOptions.length > 0) {
        const matched = studyOptions.find(
          (s: any) => String(s.value) === String(globalStudy.studyid)
        );
        if (matched) {
          setValue("study", matched.value, { shouldValidate: true, shouldDirty: true });
        }
      } else {
        setValue("study", "", { shouldValidate: true, shouldDirty: true });
      }
    }
  }, [globalStudy?.studyid, studyOptions, setValue, editId]);

  // When period changes in Add mode, reset IP category options and container selections.
  useEffect(() => {
    if (!editId) {
      setValue("ipcategory", "");
      setValue("productname", "");
      setValue("batchlotno", "");
      setValue("manufacturingdate", "");
      setValue("expirydate", "");
      setValue("manufacturerlabel", "");
      setValue("manufacturername", "");
      setValue("unsealcontainers", []);
      setDeductions({});
    }
  }, [selectedPeriodNo, setValue, editId]);

  // Populate IP Details upon category selection
  useEffect(() => {
    if (ipData) {
      setValue("productname", ipData.productname || "");
      setValue("batchlotno", ipData.batchlotno || "");
      setValue("manufacturingdate", formatDate(ipData.manufacturingdate) || "");
      setValue("expirydate", formatDate(ipData.expirydate) || "");
      setValue("manufacturerlabel", ipData.manufacturerlabeltext || "");
      setValue("manufacturername", ipData.manufacturername || "");
    } else if (!selectedIPCategoryVal) {
      setValue("productname", "");
      setValue("batchlotno", "");
      setValue("manufacturingdate", "");
      setValue("expirydate", "");
      setValue("manufacturerlabel", "");
      setValue("manufacturername", "");
      setValue("unsealcontainers", []);
    }
  }, [ipData, selectedIPCategoryVal, setValue]);

  // Populate Form with Existing Record Data (Edit Mode)
  useEffect(() => {
    if (selectedRecord && editId && !isPopulatedRef.current) {
      isPopulatedRef.current = true;
      if (selectedRecord.studyid) setValue("study", String(selectedRecord.studyid), { shouldValidate: true });
      if (selectedRecord.periodid) setValue("periodno", selectedRecord.periodid, { shouldValidate: true });
      if (selectedRecord.ipcategory) setValue("ipcategory", selectedRecord.ipcategory, { shouldValidate: true });
      const qtyVal = selectedRecord.quantitypersubject ?? selectedRecord.qtypersubject;
      if (qtyVal !== undefined && qtyVal !== null) {
        setValue("persubjectqty", qtyVal, { shouldValidate: true });
      }
    }
  }, [selectedRecord, editId, setValue]);

  // Populate existing containers in Edit Mode
  useEffect(() => {
    if (
      editId &&
      selectedRecord?.containerdetails &&
      Array.isArray(selectedRecord.containerdetails) &&
      selectedRecord.containerdetails.length > 0 &&
      availableContainers.length > 0
    ) {
      const initialSelectedNums: string[] = [];
      const initialDeductions: ContainerDeductionState = {};

      selectedRecord.containerdetails.forEach((cd: any) => {
        const match = availableContainers.find(
          (c: any) =>
            String(c.ipdetailid) === String(cd.ipdetailid) ||
            (cd.containernumber && String(c.containernumber) === String(cd.containernumber).padStart(2, "0"))
        );
        if (match) {
          initialSelectedNums.push(match.containernumber);
          initialDeductions[match.containernumber] = {
            reason: cd.deductionreason || "",
            deductedqty: cd.deductedqty ?? "",
          };
        }
      });

      if (initialSelectedNums.length > 0) {
        setValue("unsealcontainers", initialSelectedNums, { shouldValidate: true });
        setDeductions(initialDeductions);
      }
    }
  }, [editId, selectedRecord?.containerdetails, availableContainers, setValue]);

  /* ------------------------------------------------------------------------ */
  /* 6. CALCULATED QUANTITIES                                                 */
  /* ------------------------------------------------------------------------ */
  const selectedContainerDetails = useMemo(() => {
    return availableContainers.filter((c: ContainerItem) =>
      selectedContainers.includes(c.containernumber)
    );
  }, [availableContainers, selectedContainers]);

  const netUsableContainerQuantity = useMemo(() => {
    return calculateNetUsableContainerQuantity(selectedContainerDetails, deductions);
  }, [selectedContainerDetails, deductions]);

  const totalDeductedQuantity = useMemo(() => {
    return calculateTotalDeductedQuantity(selectedContainerDetails, deductions);
  }, [selectedContainerDetails, deductions]);

  const totalInitialQuantity = useMemo(() => {
    return calculateTotalInitialQuantity(selectedContainerDetails);
  }, [selectedContainerDetails]);

  const exceededContainers = useMemo(() => {
    return selectedContainerDetails.filter((c: ContainerItem) => {
      const dQty = Number(deductions[c.containernumber]?.deductedqty || 0);
      return dQty > c.initialquantity;
    });
  }, [selectedContainerDetails, deductions]);

  const missingReasonContainers = useMemo(() => {
    return selectedContainerDetails.filter((c: ContainerItem) => {
      const dQty = Number(deductions[c.containernumber]?.deductedqty || 0);
      const reason = deductions[c.containernumber]?.reason || "";
      return dQty > 0 && dQty <= c.initialquantity && !reason.trim();
    });
  }, [selectedContainerDetails, deductions]);

  /* ------------------------------------------------------------------------ */
  /* 7. EVENT HANDLERS                                                        */
  /* ------------------------------------------------------------------------ */
  const handleDeductionReasonChange = (cNum: string, reason: string) => {
    setDeductions((prev) => ({
      ...prev,
      [cNum]: {
        reason,
        deductedqty: prev[cNum]?.deductedqty ?? "",
      },
    }));
  };

  const handleDeductionQtyChange = (cNum: string, initialQty: number, qtyVal: string) => {
    let sanitized = qtyVal.replace(/[^0-9.]/g, "");
    const parts = sanitized.split(".");
    if (parts.length > 2) {
      sanitized = parts[0] + "." + parts.slice(1).join("");
    }
    setDeductions((prev) => ({
      ...prev,
      [cNum]: {
        reason: prev[cNum]?.reason ?? "",
        deductedqty: sanitized,
      },
    }));
  };

  const validateContainerDeductions = (): boolean => {
    return exceededContainers.length === 0 && missingReasonContainers.length === 0;
  };

  const deductionTableColumns: Column[] = useMemo(
    () =>
      getDeductionTableColumns(
        deductions,
        handleDeductionQtyChange,
        handleDeductionReasonChange
      ),
    [deductions]
  );

  const handleSave = () => {
    if (!selectedContainers || selectedContainers.length === 0) {
      showToastMessage("error", "Please select at least one container to unseal");
      return;
    }

    if (!validateContainerDeductions()) {
      return;
    }

    const payload = buildSaveDispensingPayload({
      ipData,
      selectedStudyId,
      perSubjectQtyVal,
      totalQtyNeededVal: null,
      selectedPeriodNo,
      selectedContainerDetails,
      deductions,
    });

    if (InsertMutation) {
      InsertMutation.mutate(payload, {
        onSuccess: (response: any) => {
          queryClient.invalidateQueries({ queryKey: ["dispensing-select-all"] });
          showToastMessage(
            "success",
            response?.message || "Dispensing Log saved successfully"
          );
          if (setIsStep1Saved) setIsStep1Saved(true);
          if (onSuccessSubmit) {
            onSuccessSubmit({
              selectedContainers,
              perSubjectQty: perSubjectQtyVal,
              netUsableContainerQuantity,
              ipData,
              selectedStudyId,
              selectedPeriodNo,
              subjectList: selectedStudy?.subjectno || [],
            });
          }
        },
        onError: (err: any) => {
          showToastMessage("error", err?.message || err || "Failed to save Dispensing Log");
        },
      });
    }
  };

  const handleUpdateClick = () => {
    if (!selectedContainers || selectedContainers.length === 0) {
      showToastMessage("error", "Please select at least one container to unseal");
      return;
    }

    if (!validateContainerDeductions()) {
      return;
    }

    setIsUpdateModalOpen(true);
  };

  const handleReasonSubmit = (reasonPayload: ReasonSubmitPayload) => {
    const payload = buildUpdateDispensingPayload({
      editId: Number(editId),
      updateReason: reasonPayload.reason,
      ipData,
      selectedStudyId,
      perSubjectQtyVal,
      totalQtyNeededVal: null,
      selectedPeriodNo,
      selectedContainerDetails,
      originalContainerDetails: selectedRecord?.containerdetails || [],
      deductions,
    });

    if (UpdateMutation) {
      UpdateMutation.mutate(payload, {
        onSuccess: (response: any) => {
          queryClient.invalidateQueries({ queryKey: ["dispensing-select-all"] });
          queryClient.invalidateQueries({ queryKey: ["dispensing-select", Number(editId)] });
          showToastMessage(
            "success",
            response?.message || "Dispensing Log updated successfully"
          );
          setIsUpdateModalOpen(false);
          if (setIsStep1Saved) setIsStep1Saved(true);
          if (onSuccessSubmit) {
            onSuccessSubmit({
              selectedContainers,
              perSubjectQty: perSubjectQtyVal,
              netUsableContainerQuantity,
              ipData,
              selectedStudyId,
              selectedPeriodNo,
              subjectList: selectedStudy?.subjectno || [],
            });
          }
        },
        onError: (err: any) => {
          showToastMessage("error", err?.message || err || "Failed to update Dispensing Log");
        },
      });
    }
  };

  const studyInformationFields = useMemo(
    () => getStudyInformationFields(studyOptions, periodOptions),
    [studyOptions, periodOptions]
  );

  const ipDetailFields = useMemo(
    () => getIpDetailFields(ipCategoryOptions, selectedPeriodNo),
    [ipCategoryOptions, selectedPeriodNo]
  );

  const isPending = InsertMutation?.isPending || UpdateMutation?.isPending;

  if (IpDropdownQuery.isLoading) {
    return <FullPageLoader />;
  }

  return (
    <>
      {/* Section 1: Study & Period Information */}
      <SectionCard
        index={1}
        icon="ri-hospital-line"
        title="Study Information"
        subtitle="Select study protocol and period to enable IP category selection"
      >
        <CommonForm
          fields={studyInformationFields}
          control={control}
          errors={errors}
          columns={2}
        />
      </SectionCard>

      {/* Section 2: IP Details */}
      <SectionCard
        index={2}
        icon="ri-capsule-line"
        title="IP Detail"
        subtitle="Investigational Product specification and manufacturing details"
      >
        <CommonForm
          fields={ipDetailFields}
          control={control}
          errors={errors}
          columns={3}
        />
      </SectionCard>

      {/* Section 3: Container Selection & Deduction Management */}
      <SectionCard
        index={3}
        icon="ri-archive-line"
        title="Container Selection & Deduction Management"
        subtitle="Select unsealed containers and record any deduction reasons or quantities"
      >
        {/* Container Multiselect */}
        <div className={`mb-2 ${styles.containerSelectionBox}`}>
          <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom border-light-subtle">
            <div className="d-flex align-items-center gap-2">
              <div className={`rounded-circle d-flex align-items-center justify-content-center bg-primary-subtle text-primary ${styles.iconCircle}`}>
                <i className="ri-box-3-line fs-5" />
              </div>
              <div>
                <Label className="fw-bold text-dark mb-0 fs-14">
                  Container Selection to unseal <span className="text-danger">*</span>
                </Label>
                <span className={`text-muted d-block small ${styles.subtitleText}`}>
                  Choose containers to unseal and allocate stock for dispensing
                </span>
              </div>
              <Badge
                color="primary"
                pill
                className="ms-2 px-2.5 py-1 fs-12 fw-semibold shadow-xs"
              >
                {selectedContainers.length} Selected
              </Badge>
            </div>

            {availableContainers.length > 0 && (
              <div className="d-flex gap-2 align-items-center">
                <Button
                  type="button"
                  color="primary"
                  outline
                  size="sm"
                  className="px-2.5 py-1 rounded-pill fs-12 fw-medium"
                  onClick={() =>
                    setValue(
                      "unsealcontainers",
                      availableContainers.map((c: ContainerItem) => c.containernumber),
                      { shouldValidate: true, shouldDirty: true }
                    )
                  }
                  disabled={!selectedIPCategoryVal}
                >
                  <i className="ri-check-double-line me-1" /> Select All
                </Button>
                <Button
                  type="button"
                  color="danger"
                  outline
                  size="sm"
                  className="px-2.5 py-1 rounded-pill fs-12 fw-medium"
                  onClick={() =>
                    setValue("unsealcontainers", [], {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                  disabled={!selectedIPCategoryVal}
                >
                  <i className="ri-close-circle-line me-1" /> Clear All
                </Button>
              </div>
            )}
          </div>

          {!selectedPeriodNo ? (
            <div className="p-3 bg-white rounded border border-light-subtle text-center text-muted small">
              <i className="ri-information-line me-1 text-primary" />
              Please select <strong>Period No.</strong> above to enable IP Category and view available containers.
            </div>
          ) : !selectedIPCategoryVal ? (
            <div className="p-3 bg-white rounded border border-light-subtle text-center text-muted small">
              <i className="ri-information-line me-1 text-primary" />
              Please select an <strong>IP Category</strong> above to view available containers.
            </div>
          ) : availableContainers.length === 0 ? (
            <div className="p-3 bg-white rounded border border-light-subtle text-center text-muted small">
              <i className="ri-alert-line me-1 text-warning" />
              No containers found for the selected IP Category.
            </div>
          ) : (
            <Controller
              name="unsealcontainers"
              control={control}
              rules={{
                required: "At least one container must be selected for unsealing",
              }}
              render={({ field: { value = [], onChange } }) => (
                <div className="d-flex flex-wrap gap-2">
                  {availableContainers.map((c: ContainerItem) => {
                    const isChecked = value.includes(c.containernumber);
                    return (
                      <div
                        key={c.containernumber}
                        className={`d-flex align-items-center gap-1.5 ${
                          isChecked ? styles.containerCardChecked : styles.containerCard
                        }`}
                        onClick={() => {
                          if (isChecked) {
                            onChange(
                              value.filter((num: string) => num !== c.containernumber)
                            );
                          } else {
                            onChange([...value, c.containernumber]);
                          }
                        }}
                      >
                        <i
                          className={
                            isChecked
                              ? "ri-checkbox-circle-fill fs-6 text-white"
                              : "ri-checkbox-blank-circle-line fs-6 text-muted"
                          }
                        />
                        <span className="fw-semibold fs-12 ms-1">
                          Container {c.containernumber}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            />
          )}

          {errors.unsealcontainers && (
            <div className="text-danger small mt-2 d-flex align-items-center gap-1">
              <i className="ri-error-warning-line" />
              {errors.unsealcontainers.message as string}
            </div>
          )}
        </div>

        {/* Selected Containers Deduction Table */}
        {selectedContainerDetails.length > 0 && (
          <div className="mb-4">
            {exceededContainers.length > 0 && (
              <Alert color="danger" className="d-flex align-items-center gap-2 py-1 px-3 mb-2 rounded-2 shadow-xs border-0 border-start border-3 border-danger">
                <i className="ri-error-warning-fill fs-5 flex-shrink-0 text-danger" />
                <div className="fs-12 text-danger-emphasis">
                  <strong className="me-1">Deduction Exceeded:</strong>
                  {exceededContainers.length === 1
                    ? `Deducted quantity for Container ${exceededContainers[0].containernumber} exceeds initial available quantity (${exceededContainers[0].initialquantity} units).`
                    : `Deducted quantity for Container ${exceededContainers.map((c: ContainerItem) => `${c.containernumber}`).join(", ")} exceeds initial available quantity.`}
                </div>
              </Alert>
            )}

            {exceededContainers.length === 0 && missingReasonContainers.length > 0 && (
              <Alert color="danger" className="d-flex align-items-center gap-2 py-1 px-3 mb-2 rounded-2 shadow-xs border-0 border-start border-3 border-danger">
                <i className="ri-error-warning-fill fs-5 flex-shrink-0 text-danger" />
                <div className="fs-12 text-danger-emphasis">
                  <strong className="me-1">Reason Required:</strong>
                  {missingReasonContainers.length === 1
                    ? `Please enter a deduction reason for Container ${missingReasonContainers[0].containernumber}.`
                    : `Please enter a deduction reason for Container ${missingReasonContainers.map((c: ContainerItem) => `${c.containernumber}`).join(", ")}.`}
                </div>
              </Alert>
            )}

            <div className={styles.deductionCard}>
              <div className="d-flex justify-content-between align-items-center mb-1 pb-0 border-bottom border-light-subtle flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-calculator-line fs-5 text-primary" />
                  <Label className="fw-bold text-dark mb-0 fs-14">
                    Container Deduction & Final Quantity Breakdown
                  </Label>
                </div>

                <div className="d-flex gap-2 align-items-center flex-wrap">
                  <span className="px-3 py-1 rounded-pill fs-12 fw-semibold bg-primary-subtle text-primary border border-primary-subtle d-inline-flex align-items-center gap-1">
                    <i className="ri-box-2-line" /> Initial: <strong>{totalInitialQuantity}</strong>
                  </span>
                  <span className="px-3 py-1 rounded-pill fs-12 fw-semibold bg-danger-subtle text-danger border border-danger-subtle d-inline-flex align-items-center gap-1">
                    <i className="ri-subtract-line" /> Deductions: <strong>{totalDeductedQuantity}</strong>
                  </span>
                  <span className="px-3 py-1 rounded-pill fs-12 fw-semibold bg-success-subtle text-success border border-success-subtle d-inline-flex align-items-center gap-1">
                    <i className="ri-checkbox-circle-line" /> Net Usable: <strong>{netUsableContainerQuantity}</strong>
                  </span>
                </div>
              </div>

              <CommonTableIndexWise
                columns={deductionTableColumns}
                data={selectedContainerDetails}
                hideAction={true}
                paginationShow={false}
              />
            </div>
          </div>
        )}

        {/* Quantity to Dispense Per Subject Field */}
        <Row className="mt-3">
          <Col md={6}>
            <CommonForm
              fields={getPerSubjectQtyFields()}
              control={control}
              errors={errors}
              columns={1}
            />
          </Col>
        </Row>
      </SectionCard>

      {/* Sticky Action Footer */}
      <div className={formStyles.stickyFooter}>
        <div className="d-flex justify-content-end gap-2 w-100">
          <Button
            type="button"
            color="primary"
            outline
            disabled={
              editId
                ? UpdateMutation?.isPending
                : isStep1Saved || InsertMutation?.isPending
            }
            onClick={handleSubmit(editId ? handleUpdateClick : handleSave)}
          >
            {isPending && <Spinner size="sm" className="me-2" />}
            {editId ? "Update" : "Save"}
          </Button>
          <Button
            type="button"
            color="primary"
            outline
            disabled={!editId && !isStep1Saved}
            onClick={() => onStepChange(2)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Update Reason Modal */}
      <Reason
        isOpen={isUpdateModalOpen}
        toggle={() => setIsUpdateModalOpen(false)}
        action="Update Record"
        id={Number(editId)}
        click={handleReasonSubmit}
        isLoading={UpdateMutation?.isPending}
      />
    </>
  );
};

export default AddContainerSelection;
